import { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useOperatorEfficiency,
  type OperatorEfficiencyData,
  type OperatorStats,
} from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";

interface OperatorTableProps {
  dateRange: DateRange;
}

function TrendIcon({ trend }: { trend: "up" | "down" | "stable" }) {
  if (trend === "up") return <TrendingUp className="h-4 w-4 text-green-600" />;
  if (trend === "down")
    return <TrendingDown className="h-4 w-4 text-red-600" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function EfficiencyBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? "bg-green-100 text-green-800"
      : score >= 60
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-800";

  return (
    <Badge className={cn("font-mono", color)} variant="outline">
      {score}
    </Badge>
  );
}

function OperatorRow({ operator }: { operator: OperatorStats }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <TableCell>
          <Button variant="ghost" size="sm" className="p-0 h-auto">
            {isOpen ? (
              <ChevronUp className="h-4 w-4 mr-2" />
            ) : (
              <ChevronDown className="h-4 w-4 mr-2" />
            )}
            <span className="font-medium">{operator.operatorName}</span>
          </Button>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {operator.depot}
        </TableCell>
        <TableCell className="text-right">
          <span className="text-green-600">{operator.tasks.completed}</span>
          <span className="text-muted-foreground"> / </span>
          <span>{operator.tasks.total}</span>
        </TableCell>
        <TableCell className="text-right">
          {operator.shipmentsHandled > 0 ? (
            <span>{operator.shipmentsHandled}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          {operator.totalTimeMinutes > 0 ? (
            <span>
              {Math.floor(operator.totalTimeMinutes / 60)}h{" "}
              {Math.round(operator.totalTimeMinutes % 60)}m
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <span
            className={cn(
              operator.successRate >= 90
                ? "text-green-600"
                : operator.successRate >= 70
                  ? "text-yellow-600"
                  : "text-red-600"
            )}
          >
            {operator.successRate.toFixed(1)}%
          </span>
        </TableCell>
        <TableCell className="text-center">
          <EfficiencyBadge score={operator.efficiencyScore} />
        </TableCell>
        <TableCell className="text-center">
          <TrendIcon trend={operator.trend} />
        </TableCell>
      </TableRow>
      {isOpen && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={8}>
            <div className="py-2 px-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">In Progress: </span>
                  <span className="text-blue-600">
                    {operator.tasks.inProgress}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Pending: </span>
                  <span>{operator.tasks.pending}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Failed: </span>
                  <span className="text-red-600">{operator.tasks.failed}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Avg Task: </span>
                  <span>
                    {operator.avgTaskTimeMinutes !== null
                      ? `${operator.avgTaskTimeMinutes} min`
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Time: </span>
                  <span>{operator.totalTimeMinutes} min</span>
                </div>
              </div>
              {operator.failureReasons.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm text-muted-foreground">
                    Failure Reasons:{" "}
                  </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {operator.failureReasons.map((fr) => (
                      <Badge
                        key={fr.code}
                        variant="outline"
                        className="text-red-600"
                      >
                        {fr.reason} ({fr.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BenchmarksSummary({
  benchmarks,
}: {
  benchmarks: OperatorEfficiencyData["benchmarks"];
}) {
  return (
    <div className="flex flex-wrap gap-6 text-sm text-muted-foreground mb-4">
      <div>
        <span>Avg Tasks/Operator: </span>
        <span className="font-medium text-foreground">
          {benchmarks.avgTasksPerOperator}
        </span>
      </div>
      <div>
        <span>Avg Task Time: </span>
        <span className="font-medium text-foreground">
          {benchmarks.avgTaskTimeMinutes} min
        </span>
      </div>
      <div>
        <span>Avg Success Rate: </span>
        <span className="font-medium text-foreground">
          {benchmarks.avgSuccessRate.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export default function OperatorTable({ dateRange }: OperatorTableProps) {
  const { data, isLoading, error } = useOperatorEfficiency(dateRange);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Operator Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Operator Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Failed to load operator data
          </p>
        </CardContent>
      </Card>
    );
  }

  const efficiencyData = data;
  const operators = efficiencyData?.operators ?? [];

  if (operators.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Operator Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No operator data available for this period
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Operator Performance</span>
          <span className="text-sm font-normal text-muted-foreground">
            {operators.length} operators
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <BenchmarksSummary benchmarks={efficiencyData!.benchmarks} />
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Operator</TableHead>
                <TableHead>Dispatch center</TableHead>
                <TableHead className="text-right">Tasks</TableHead>
                <TableHead className="text-right">Shipments</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Success</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="text-center">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operators.map((operator) => (
                <OperatorRow key={operator.operatorId} operator={operator} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
